import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgModalAssistantService } from '@libs/frameworks/angular';
import { filter, Subscription, take } from 'rxjs';

import { IUpdateAnnouncement, IUpdateAnnouncementFeed } from '../../../services/shared/update-announcements/update-announcement.interface';
import { UpdateAnnouncementFeedService } from '../../../services/shared/update-announcements/update-announcement-feed.service';
import { UpdateAnnouncementDisplayService } from '../../../services/shared/update-announcements/update-announcement-display.service';


@Component({
	selector: 'app-update-banner',
	templateUrl: './update-banner.component.html',
	styleUrls: ['./update-banner.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class UpdateBannerComponent implements AfterViewInit, OnDestroy {

	@ViewChild('announcement', { static: true }) announcement: TemplateRef<unknown>;

	//** Remote Configuration */
	private feed?: IUpdateAnnouncementFeed;

	//** UI Variables */
	updates		: IUpdateAnnouncement[] = [];
	activeIndex	: number = 0;
	isVisible	: boolean = false;

	//** Modal State */
	private openTimer		  		: ReturnType<typeof setTimeout>;
	private pendingModal	  		: Subscription;
	private modalRef		  		: NgbModalRef;
	private userId					: string;
	private requestVersion			: number = 0;
	private destroyed				: boolean = false;
	private presentedThisVisit		: boolean = false;
	private readonly subscriptions 	: Subscription = new Subscription();


	constructor(
		@Inject(DOCUMENT) private readonly document: Document,
		private readonly modalAssistant		: NgModalAssistantService,
		private readonly modalService		: NgbModal,
		@Inject(UpdateAnnouncementFeedService) private readonly updateAnnouncementFeedService: UpdateAnnouncementFeedService,
		private readonly updateAnnouncementDisplayService: UpdateAnnouncementDisplayService,
		private readonly ngZone			: NgZone,
		private readonly changeDetectorRef	: ChangeDetectorRef
	) {}


	/**------------------------------------------------------
	 * Lifecycle Hooks
	 */
	ngAfterViewInit(): void {
		this.userId = this.updateAnnouncementDisplayService.getUserId();
		//0 - accept manual requests independently from automatic presentation history
		this.subscriptions.add(this.updateAnnouncementDisplayService.openRequests$.subscribe(() => {
			this.handleManualOpen();
		}));

		//1 - defer feed initialization until the dashboard view has finished rendering
		this.openTimer = setTimeout(() => {
			this.updateAnnouncementFeedService.startVisit();
			this.subscriptions.add(this.updateAnnouncementFeedService.feed$.subscribe((feed: IUpdateAnnouncementFeed) => {
				this.handleFeed(feed);
			}));
		}, 0);
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.cancelPending();
		clearTimeout(this.openTimer);
		this.subscriptions.unsubscribe();
		this.modalRef?.close();
	}


	/**------------------------------------------------------
	 * UI Helpers
	 */
	get activeUpdate(): IUpdateAnnouncement {
		return this.updates[this.activeIndex];
	}

	dismiss(): void {
		this.cancelPending();
		this.isVisible = false;
		this.modalRef?.close();
	}

	goToUpdate(index: number): void {
		if (index < 0 || index >= this.updates.length) return;
		this.activeIndex = index;
		this.changeDetectorRef.detectChanges();
		const content: HTMLElement | null = this.document.getElementById('flying-update-content');
		if (content) content.scrollTop = 0;
	}


	/**------------------------------------------------------
	 * Announcement Initialization
	 */
	private handleManualOpen(): void {
		if (this.isVisible || !this.feed?.enabled || !this.feed.badgeEnabled || !this.updates.length) return;

		this.activeIndex = 0;
		this.requestAnnouncement(false);
	}

	private handleFeed(feed: IUpdateAnnouncementFeed): void {
		this.feed = feed;
		this.updates = feed.updates;
		this.cancelPending();
		if (!feed.enabled || !feed.updates.length || (!feed.bannerEnabled && !feed.badgeEnabled)) {
			this.dismiss();
			return;
		}
		if (this.isVisible || !feed.bannerEnabled) return;

		this.activeIndex = 0;
		this.requestAnnouncement();
	}


	/**------------------------------------------------------
	 * Modal Helpers
	 */
	private requestAnnouncement(automatic: boolean = true): void {
		if (this.destroyed || !this.activeUpdate || this.isVisible || (automatic && this.presentedThisVisit)) return;
		this.cancelPending();
		const version 		: number = this.requestVersion;
		const updateIds 	: string[] = this.updates.map((update: IUpdateAnnouncement) => update.id);
		const attemptOpen 	: () => void = () => {
			this.updateAnnouncementDisplayService.show(this.userId, updateIds, automatic, () => this.ngZone.run(() => {
				if (this.destroyed || version !== this.requestVersion || this.isVisible || !this.feed?.enabled) return false;
				if (automatic ? !this.feed.bannerEnabled : !this.feed.badgeEnabled) return false;
				if (automatic && this.presentedThisVisit) return false;
				// Another startup dialog can appear while this tab is waiting for the browser lock.
				if (this.modalService.hasOpenModals()) {
					this.requestAnnouncement(automatic);
					return false;
				}
				this.openAnnouncement();
				return true;
			}), this.feed ? this.feed.displayLimit : 1, () => this.modalRef.result).catch((error: unknown) => {
				console.warn('Update announcement could not be opened.', error);
			});
		};
		if (!this.modalService.hasOpenModals()) {
			attemptOpen();
			return;
		}
		this.pendingModal = this.modalService.activeInstances.pipe(
			filter((modals: NgbModalRef[]) => modals.length === 0),
			take(1)
		).subscribe(attemptOpen);
		this.subscriptions.add(this.pendingModal);
	}

	private openAnnouncement(): void {
		this.modalRef = this.modalAssistant.openLarge(this.announcement, {
			centered 		: true,
			scrollable 		: true,
			backdrop 		: true,
			keyboard 		: true,
			ariaLabelledBy 	: 'flying-update-title',
			ariaDescribedBy : 'flying-update-content'
		}).getModal();

		this.isVisible = true;
		this.presentedThisVisit = true;
		this.changeDetectorRef.markForCheck();
		const onClose: () => void = () => { this.isVisible = false; };
		this.modalRef.result.then(onClose, onClose);
	}


	/**------------------------------------------------------
	 * Cancel Pending Requests
	 */
	private cancelPending(): void {
		this.requestVersion++;
		this.pendingModal?.unsubscribe();
	}
}
